import type {ReactNode} from 'react'
import {NavLink} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {LockGlyph} from '@/components/ui/icons'
import {AmberDot} from '@/components/amber-dot'
import {SectionActiveBeacon} from '@/components/sidebar-section'
import {cn} from '@/lib/utils'

/** U17: el punto ámbar y el candado llevan texto `.sr-only`; el punto activo no, porque `NavLink` ya
 * pone `aria-current="page"`. */
export function SidebarLink({
  to,
  end,
  testId,
  title,
  locked,
  showAlert,
  indented,
  children,
}: {
  to: string
  end?: boolean
  testId?: string
  title?: string
  locked?: boolean
  showAlert?: boolean
  indented?: boolean
  children: ReactNode
}) {
  const {t} = useTranslation()
  return (
    <NavLink
      to={to}
      end={end}
      title={title}
      data-testid={testId}
      className={({isActive}) =>
        cn(
          // `min-h-11`: 44px minimum tap target in the mobile drawer; the md+ rail is mouse-driven.
          'sidenav-link flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:min-h-0',
          indented && 'pl-6 text-[13px]',
          isActive && 'is-active'
        )
      }>
      {({isActive}) => (
        <>
          <SectionActiveBeacon active={isActive} />
          <span
            aria-hidden="true"
            className={cn('h-[5px] w-[5px] shrink-0 rounded-full', isActive ? 'bg-current' : 'bg-transparent')}
          />
          <span className="min-w-0 flex-1 truncate">{children}</span>
          {showAlert && (
            <AmberDot label={t('common.senal.pendiente')} testId={testId ? `${testId}-alert` : undefined} />
          )}
          {locked && (
            <span data-testid={testId ? `${testId}-lock` : undefined} className="shrink-0 opacity-55">
              <LockGlyph />
              <span className="sr-only">{t('common.senal.bloqueado')}</span>
            </span>
          )}
        </>
      )}
    </NavLink>
  )
}
